import { keyframes } from '@emotion/css';
import { Borders } from 'Shared/backend/widgets/Borders';
import { Margins } from 'Shared/backend/widgets/Margins';
import { Padding } from 'Shared/backend/widgets/Padding';
import { Brightness } from 'Shared/themes/Brightness';
import { Colour } from 'Shared/themes/Colour';
import { ShowForegroundOnTimer } from 'Shared/themes/ShowForegroundOnTimer';
import { CancelStylesUi } from 'Shared/themes/CancelStylesUi';
import { ClassesUi } from 'Shared/themes/ClassesUi';
import { theme } from 'Shared/frontend/SettingsProvider';
import { stickyStyle } from 'Shared/frontend/nav/StandardNavWithLogo';


export interface IAnimation {
	type: 'enable'|'enableOnce';
	options?: any;
	css?: any;
};

export interface IStylesRule {
	location: string[],
	css?: any,
	ignorePageMargins?: boolean,
	parallax?: boolean,
	border?;
	padding?;
	margin?;

	animations?: IAnimation[];
};

type Styles = {
	ui: XXX, //COMPONENT

	rules: (params:any) => {
		[name:string]: IStylesRule[]
	}
};


//TODO? structure as a category tree if the concept appears to work. This should allow names to be scoped within their categories and subcategories...

export function standardStyles(sizes,text): Styles
{
//TODO Probably better to have key/label pairs...
//XXX could have children to allow locations to specify sublocations. Probably better to implement eventually.
//XXX maybe support as 'posters:events' & 'posters:specials'
//XXX PROBABLY better done on a site basis... for the most part  TODO


	return {
/*				
		'No image margins on mobile': {
			rules: () => [{
				location: ['**','image'],
				noMarginsOnMobile: true
			}]
		},
*/				

//TODO probably  place styles with categories so we don't have to put the category in the style names
		'Fade in background': {
			category: 'nav',
			explanation: 'Show the nav background when the page is not scrolled to the top.',

			rules: (atTop:boolean) => [{
				location: ['nav'],
				css: {
					...stickyStyle(),
					position: 'fixed',
					transition: 'background-color 0.7s',
					backgroundColor: atTop ? 'rgba(0,0,0,0)' : theme().nav.desktop.settings.outer.backgroundColor
				}
			}]
		},

		'Hide desktop nav': {
			category: 'nav',
			explanation: 'Hides the desktop navigation bar.',

			rules: (_atTop:boolean) => [{
				location: ['nav'],
				hide: true
			}]
		},

		'Hide footer': {
			category: 'page',
			explanation: 'Hides the page footer.',

			rules: () => [{
				location: ['footer'],
				hide: true
			}]
		},

		'Page title': {
			category: 'page',
			explanation: 'Converts a title to page title.',

			rules: () => [{
				location: ['text:title'],
				css: text.pageTitle
			}]
		},

		'Ignore page margins': {
			category: 'layout',
			explanation: 'Allows the contents to extend to the edges of the page. Useful for banner images.',

			rules: () => [{
				location: ['*'],
				ignorePageMargins: true
			}]
		},
		'Parallax': {
			category: 'transitions',
			explanation: 'Scroll the contents at a different rate to the page scroll. Useful effect when applied to images that have text or logos overlayed. Sometimes used in banners and featured items.',

			rules: () => [{
				location: ['*'],
				parallax: true
			}]
		},
	
		'Brightness': {
			category: 'custom',
			explanation: 'Change the brightness of an image.',
			ui: Brightness,

			rules: params => [{
				location: ['**','image'],

				css: { 
					filter: `brightness(${params.brightness}%)`
				},
			}]
		},

		'Background colour': {
			category: 'custom',
			explanation: 'Set the background colour.',
			ui: Colour,

			rules: params => [{
				location: ['**'],   //XXX unsure about **, cf * & maybe * *
				css: { 
					background: params.colour
				},
			}]
		},

		'Overlay foreground background': {
			category: 'overlays',
			explanation: 'Darkens the background of foreground image of an overlay. [unused. delete.]',
			rules: () => [{
				location: ['**','overlay','* foreground'],
				padding: {sides:'all',all:'2em'},
				css: {
					backgroundColor: 'rgba(0, 0, 0, 0.56)'
				}
			}]
		},
		
		//XXX modelled off The Townie club page.  XXX USED?
		'Narrow page': {
			category: 'layout',
			explanation: 'Makes page narrower. Apply at the page level. Sometimes improves the appearance of the content.',
			rules: () => [{
				location: ['^','*'],
				css: {
					maxWidth: 820,
					marginLeft: 'auto',
					marginRight: 'auto',
				}
			}]
		},

		'Responsive embed': {		//XXX cf breaking down into components? eg separate out iframe part
			category: 'layout',   //XXX or 'embed'?
			exploration: 'Embed uses 100% width and most the height of a desktop.',
			rules: () => [{
				location: ['*'],
				css: {
					width: '100%',
					display: 'inline-block',
					overflow: 'hidden',
					paddingBottom: '56.25%',
					position: 'relative',
					marginTop: '1em',
					height: 0,

					iframe: {
						left: 0,
						top: 0,
						height: '100%',
						width: '100%',
						position: 'absolute'
					}
				}
			}]
		},

		'Contact page top': {
			category: 'layout', 
			exploration: 
				`Lays out the venue image, hours, and contact details is a space-efficient manner. 
				Requires three items in a vertical layout: venue image, then hours, then contact details.`,
			rules: () => [{
				location: ['*'],
				css: {
					['> *:first-child']: {
						width: '100%',
						marginBottom: '1em',

						[`@container (min-width: ${sizes.minDesktopWidth}px)`]: {
							width: '50%',
							float: 'left',
							paddingRight: '2em',
						},
					},
					['> *:nth-child(2)']: {
						width: '100%',
						marginBottom: '1em',

						[`@container (min-width: ${sizes.minDesktopWidth}px)`]: {
							textAlign: 'left'
						},

						[`@container (min-width: ${sizes.flipToMobileNav}px)`]: { 
							width: '50%',
							float: 'right'
						},
						h2: {
							marginTop: 0,

							[`@container (min-width: ${sizes.minDesktopWidth}px)`]: {
								/* Trim the empty space from the text so the top aligns with the venue photo */
								marginTop: -12
							}
						}
					},
					['> *:last-child']: {
						width: '100%',

						[`@container (min-width: ${sizes.minDesktopWidth}px)`]: { 
							textAlign: 'left'
						},

						[`@container (min-width: ${sizes.flipToMobileNav}px)`]: { 
							width: '50%',
							float: 'right'
						},

						margin: '1em 0',
						display: 'flex',
						flexDirection: 'column',
						gap: '1em'
					}
				}
			}]
		},

		'Centre object': {
			category: 'custom',
			explanation: 'Centers the contents vertically and horizontally within its parent.',

			rules: () => [{
				location: ['*'],
				css: {
					height: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}
			}]
		},
	
		//XXX use in The Townie club page description. Should descriptions be left justified by default?
		'Left align': {
			category: 'custom',
			explanation: 'Aligns the text to the left. Note children inherit text aligment by default.',

			rules: () => [{
				location: ['*'],
				css: {
					textAlign: 'left'
				}
			}]
		},

		'Margins': {
			category: 'custom',
			explanation: 'Add margins.',

			ui: Margins,

			rules: (params = {}) => [{
				location: selectLocation(params.selector),
				...params
			}]
		},

		'Padding': {
			category: 'custom',
			explanation: 'Add padding to the children.',

			ui: Padding,

			rules: (params = {}) => [{
				location: selectLocation(params.selector),
				...params
			}]
		},

		'Borders': {
			category: 'custom',
			explanation: 'Add borders to the children.',

			ui: Borders,

			rules: (params = {}) => [{
				location: selectLocation(params.selector),
				...params
			}]
		},

		'Footer grid': {
			category: 'footers',
			explanation: 'Takes a grid containing footer-style information and formats it with coloured block overlays.',

			rules: () => [{
				location: ['*'],
				css: {
					justifyItems: 'stretch',
					columnGap: 0,
					rowGap: 0,
					textAlign: 'left',
					

					title: {
						fontSize: '1em',
					},
					
					'> :nth-child(n)': {
						padding: '20px',
						margin: 0,
					},
				
					'> :first-child': {
						title: {
							fontSize: '1em',
						},
						borderStyle: 'solid none solid solid',
					},
					'> :nth-child(2)': {
						borderStyle: 'solid none solid none',
					},
					'> :nth-child(3)': {
						borderStyle: 'solid solid solid none',
					},
					'> :nth-child(4)': {
						borderStyle: 'solid solid solid none',
					},
					'> :nth-child(5)': {
						borderStyle: 'none none solid solid',
					},
					'> :nth-child(6)': {
						borderStyle: 'none none solid none',
					},
					'> :nth-child(7)': {
						borderStyle: 'none none solid solid',
					},
					'> :nth-child(8)': {
						borderStyle: 'none solid solid none',
					},

					[`@container (max-width:1000px)`]: {
						//safer to flip to one column sooner
						gridTemplateColumns: '1fr',
						gridTemplateRows: 'repeat(8)',

						'> :first-child': {
							borderStyle: 'solid none none none',
							padding: '20px 0 0 20px',
						},
						'> :nth-child(2)': {
							borderStyle: 'none',
						},
						'> :nth-child(3)': {
							borderStyle: 'none',
						},
						'> :nth-child(4)': {
							borderStyle: 'solid none',
						},
						'> :nth-child(5)': {
							borderStyle: 'none',					
						},
						'> :nth-child(6)': {
							borderStyle: 'none',
							padding: '0px 20px 20px 20px',
						},
						'> :nth-child(7)': {
							borderStyle: 'solid none none none',
						},
						'> :nth-child(8)': {
							borderStyle: 'none none solid none',
						},
					},

					[`@container (max-width:${sizes.flipToOneColumn}px)`]: {
						gridTemplateColumns: '1fr',
						gridTemplateRows: 'repeat(8)'
					},
				}
			}]
		},

		'Fade in': {
			explanation: 'Fade the child in the first time it scrolls into view.',
			category: 'transitions',

			rules: () => [{
				location: ['*'],

				css: {
					transition: 'opacity 1s',
					opacity: 0.1,
				},

				animations: [{
					type: 'enableOnce',
					options: {threshold: 0.3},
					css: {
						opacity: 1.0,
					}
				}]
			}]
		},

		'Fade in foreground on hover': {
			category: 'transitions',
			explanation: 'Fade in the foreground of an overlay on hover.',

			//XXX cf defaults
			rules: params => [{
				location: ['*'],

				css: { 
					[`@container (min-width:${sizes.flipToOneColumn}px)`]: {
						'> :nth-child(2)': {

							opacity: 0, //CJ added to hide foreground until hover. 0.2 ease slows the 'hide' when hover is taken away
							transition: 'opacity .5s',

							'&:hover': {
								animationName: 'unset',
								opacity: '1.0',
									transition: 'opacity 0.2s',
							}
						}
					},

					[`@container (max-width:${sizes.flipToOneColumn}px)`]: {
						'> :nth-child(2)': {
							opacity: 0.1,
							transition: 'opacity 0.2s',
						}
					},
				},

				animations: [{
					type: 'enable',
					options: {threshold: 0.6},
					css: {
						[`@container (max-width:${sizes.flipToOneColumn}px)`]: {
							'> :nth-child(2)': {
								opacity: 1.0,
								transition: 'opacity 0.5s',
							}
						}
					}
				}]
			}]
		},


		///

		'Show foreground on timer': {
			category: 'transitions',
			explanation: 'Shows and hides the foregrounds of the overlays in a grid based on a timer.',

			ui: ShowForegroundOnTimer,
			//XXX cf defaults

			rules: params => [{
				location: ['*'],

				css: { 
					'> :nth-child(2)': {
						transition: 'opacity 1s',
					},

					[`@container (min-width:${sizes.flipToOneColumn}px)`]: {
						'> :nth-child(2)': {
							...timedFades(params.groupSize,params.cycleTime,params.startingPosition),

							//HACK hover causes the animation to reset so if there are a group of items they will no longer
							//     be coordinated. I believe you can have multiple animations a once. Consider try setting a second
							//     animation on hover...
							'&:hover': {
								animationName: 'unset',
								opacity: '1.0',
							}
						}
					},

					[`@container (max-width:${sizes.flipToOneColumn}px)`]: {
						'> :nth-child(2)': {
							opacity: 0.1,
						}
					},
				},

				animations: [{
					type: 'enable',
					options: {threshold: 0.8},
					css: {
						[`@container (max-width:${sizes.flipToOneColumn}px)`]: {
							'> :nth-child(2)': {
								opacity: 1.0,
							}
						}
					}
				}]
			}]
		},

		'Parent styles to cancel': {
			category: 'tools',
			explanation: 'Remove styles inherited from the site or parent sections.',
			ui: CancelStylesUi,
			rules: () => []
		},

		'Classes': {
			category: 'tools',
			explanation: 'Apply classes to this component for use in style location matching.',
			ui: ClassesUi,
			rules: () => []
		},
	};
}

/* startingPosition starts at 1 */
function timedFades(groupSize:number,itemCycleTime/*secs*/:number,startingPosition:number)
{
	const startPos = startingPosition - 1;

//		` + ` ${groupSize * itemCycleTime}s infinite`,
	return {
		animation: keyframes`
			0%  { opacity: 0; }  
			${20/groupSize}% { opacity: 1; } 
			${80/groupSize}% { opacity: 1; } 
			${100/groupSize}% { opacity: 0; }  
			100% { opacity: 0; }  
		` + ` ${groupSize * itemCycleTime}s infinite`,
		animationDelay: (itemCycleTime * startPos) + 's',
	};
}

function selectLocation(selector:'self'|'children'|undefined)
{
	if (!selector)
		return ['*'];

	switch (selector) {
		case 'self': return ['*'];
		case 'children': return ['*','*'];
	}
}
